import type { PostHog } from "posthog-js";

/**
 * Cap on how long the landing pageview waits for the geo lookup. The /api/geo
 * edge lookup typically resolves in tens of ms; past the cap the pageview is
 * captured without geo rather than risking losing it to a fast bounce.
 */
export const LANDING_PAGEVIEW_GEO_TIMEOUT_MS = 400;

/**
 * Enables the SDK's native history-change tracking immediately, then captures
 * the landing $pageview manually. posthog.init must have run with
 * capture_pageview: false — that only suppresses the SDK's init-time initial
 * pageview (the capture path inside init has already run), so flipping the
 * config here cannot retrigger it, while SPA navigations are tracked from the
 * first moment rather than only after the geo wait.
 *
 * For cookieless sessions the landing capture waits (bounded) on the geo
 * lookup so the before_send hook can attach $geoip_* properties to the very
 * first pageview; consented sessions pass geoPromise: null and capture
 * immediately (PostHog's server-side GeoIP enriches those). If a navigation
 * happens during the wait, history tracking has already captured the new
 * page, so the stale landing capture is skipped instead of firing with the
 * wrong URL.
 *
 * set_config does not restart history monitoring in posthog-js (its reactive
 * restart list omits historyAutocapture), hence the explicit startIfEnabled —
 * which is idempotent, so it is safe to call when the config write is skipped.
 * Manual $pageview captures flow through the SDK's PageViewManager just like
 * SDK-fired ones, so $prev_pageview_* web-analytics continuity is preserved.
 *
 * shouldCapture is re-checked after the wait and vetoes a stale capture. The
 * caller uses it to abandon an attempt whose identity has since been replaced by
 * a consent transition — otherwise this would fire against whichever identity
 * happens to be current when the wait ends, not the one it was started for.
 *
 * Resolves to whether a $pageview was actually captured.
 */
export async function captureLandingPageview(
  posthog: PostHog,
  {
    geoPromise,
    timeoutMs = LANDING_PAGEVIEW_GEO_TIMEOUT_MS,
    shouldCapture,
  }: {
    geoPromise: Promise<unknown> | null;
    timeoutMs?: number;
    shouldCapture?: () => boolean;
  }
): Promise<boolean> {
  // Only write the config when it actually needs changing. set_config rebuilds
  // the SDK's sessionPersistence unconditionally, and the replacement starts
  // empty and repopulates itself from storage. While opted out, persistence is
  // disabled: register() still updates props in memory but save() is a no-op,
  // so nothing was ever written for the rebuild to find. On the withdrawal path
  // that silently discarded the campaign params applyPerformanceConsent had
  // just restored. The config only needs writing on the init path, where it is
  // still false; after a consent transition it is already "history_change".
  if (posthog.config.capture_pageview !== "history_change") {
    posthog.set_config({ capture_pageview: "history_change" });
  }
  posthog.historyAutocapture?.startIfEnabled();

  // Pathname, not full URL: history tracking only captures on pathname
  // changes, so a query-only change during the wait leaves this capture as
  // the sole (still correct) pageview.
  const landingPathname = window.location.pathname;
  if (geoPromise) {
    await Promise.race([
      geoPromise,
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }
  if (window.location.pathname !== landingPathname) {
    return false;
  }
  if (shouldCapture && !shouldCapture()) {
    return false;
  }
  posthog.capture("$pageview");
  return true;
}
